// Pure dispatch logic for the decide rail.
//
// Imports nothing but `RECON_VERBS` (itself a pure module), so this
// is root-safe in the node pool — vitest can pick it up under the
// root tsconfig without dragging api.ts's DOM-typed fetch /
// import.meta.env into the test program. Same convention as
// household-cluster-predicate.ts / recon-verbs.ts / queue-speed.ts.
//
// `applicability(issue)` answers a narrow question: can the steward
// one-click the recommended action on this issue from the rail, and
// if so, what params does the backend need? The rail consumes the
// return shape and either renders an Apply button or a "needs full
// review" explainer (UX rule #5 — no dead end).

import { RECON_VERBS } from "./recon-verbs";

export type Verb =
  | "merge"
  | "dismiss"
  | "snooze"
  | "recase"
  | "archive"
  // The empty-household alias of "archive" (structure.ts, 2026-09-13).
  // Same server handler; named separately so it alone could join
  // AUTO_EXECUTABLE_VERBS without promoting every other archive detector.
  | "archive-empty-household"
  // Nate 2026-07-06 — one-click "I've reviewed this informational flag;
  // nothing about the record needs to change; clear it." Distinct from
  // dismiss ("not an issue") so the audit trail can tell an acknowledged
  // expected-migration-gap apart from a genuine false positive. Used by
  // the realty BrokerMint↔AC orphan detectors, where the honest action
  // is "acknowledge and move on", not a manual link that doesn't exist.
  | "acknowledge"
  // Baker 2026-05-29 — duplicate-cluster escape hatch for
  // "one of these isn't actually a person, it's a vendor
  // (title officer, lender, partner) that ended up in the
  // contacts table by mistake." Retags the chosen record's
  // `contact_type='vendor'` so it stops appearing in person-
  // facing surfaces + the duplicate detector stops emitting
  // it as a cluster member on the next reconcile.
  | "retag-as-vendor"
  // 2026-09-11 — a household has a real product with a company but no
  // household_company_relationships row (the row every count reads).
  // One click: the backend re-derives the row from the pair's records
  // (reconcileRelationshipStateForPair); no params beyond the pair.
  | "create-relationship-row"
  | "resolve-trust-reattach"
  // Grant 2026-08-18 — "the carrier sent a policy we don't have": create
  // it. Like realty's `match`, the one click cannot COMPLETE on its own
  // (a policy needs a household, and choosing it is the judgment the
  // item exists for), so the rail opens a picker instead of POSTing a
  // param-less verb. See ivans-create-policy-predicate.ts.
  | "create-policy"
  // E1 — reconciliation action vocabulary. Disposition verbs: the
  // steward records a decision and the issue resolves (audited,
  // 60s-undoable via reopen). Value-change writes (adjust /
  // accept-proposed) ride the owning domain's calc/approve workflow
  // — the company system stays the source of truth.
  | "match"
  | "accept-variance"
  | "adjust"
  | "verify"
  | "mark-historical"
  | "keep-current"
  | "accept-proposed";

// Minimal subset of CleanupIssueSummary this dispatch reads. The real
// `CleanupIssueSummary` from web/src/api.ts is a structural superset
// of `IssueLike`, so the DecideRail wrapper passes it through
// unchanged.
export interface IssueLike {
  recommendation: {
    verb: string;
    label?: string;
    params?: Record<string, unknown>;
  } | null;
  affectedRecords: Array<{ kind: string; id: string; label?: string }>;
  // Optional — lets slug-keyed special cases (client-since) apply even
  // to issues materialized before their detector's verb changed.
  detectorSlug?: string;
}

export interface ApplicabilityResult {
  verb: Verb;
  ok: boolean;
  why?: string;
  params?: Record<string, unknown>;
  // True when the issue carries NO automatic recommendation at all
  // (e.g. a married couple who share a phone — flagged for a human
  // look, but not a duplicate to merge). This is NOT a dead end: the
  // rail shows "research, then choose an action" and the disposition
  // actions (keep separate / snooze / vendor) are all one-click from
  // here. The rail uses this flag to suppress the "needs full review"
  // explainer for the empty case, which otherwise read as the
  // confusing 'The recommended action ("undefined") needs the full
  // review screen'. — Baker 2026-06-26
  noRecommendation?: boolean;
}

// Can we safely one-click Apply this verb? merge only for an exactly-
// 2-record same-kind cluster (the dominant dedup case). Bigger
// clusters / workflow entities need full review; we say so (no dead
// end) rather than half-acting.
//
// For merge: a detector can supply `recommendation.params.survivor_id`
// to disambiguate which record survives. Without it we fall back to
// recs[0]=survivor, recs[1]=merged (the historical convention,
// correct for order-agnostic dedup detectors like email/phone). The
// mortgage stub-household detector emits [stub, candidate] with the
// candidate as survivor — recs[0]=survivor would absorb the real
// household INTO the stub, which is backwards. See
// `MergeRecommendationParams` in shared/db/cleanup-detectors/shared.ts.
export function applicability(issue: IssueLike): ApplicabilityResult {
  const rec = issue.recommendation?.verb;
  // No automatic recommendation at all (couple sharing a phone, two
  // unrelated people on an office line, …). Return a one-click-able
  // dismiss base but flag the empty state so the rail does NOT render a
  // "needs full review" dead-end — the steward just picks a disposition
  // from "or do something else". Without this branch, `rec` is undefined
  // and the catch-all below printed 'The recommended action
  // ("undefined") needs the full review screen', which is exactly the
  // dead end Baker hit. — Baker 2026-06-26
  if (!rec) {
    return { verb: "dismiss", ok: false, noRecommendation: true };
  }
  // Nate 2026-07-02 — client-since issues one-click as "verify" (the
  // rail supplies the date input; the backend writes + resolves). Keyed
  // on the SLUG, not the frozen verb, so issues materialized before the
  // detector's verb changed from "review" work immediately instead of
  // dead-ending until the next reconcile refreshes their recommendation.
  if (issue.detectorSlug === "hcr-missing-client-since") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Joy 2026-07-01 — the insurance expiration issues one-click as
  // "verify", and the rail supplies the date input (isExpirationIssue).
  // Slug-gate them like client-since (finding 8): the generic RECON_VERBS
  // branch below marks "verify" one-click-applicable WITH NO params, which
  // for ins-missing-expiration is a guaranteed 400 (the backend requires
  // expiration_date). Gating on the slug makes the intent explicit and
  // carries the recommendation params, so any consumer of `app` sees the
  // record context rather than an empty verify.
  if (
    issue.detectorSlug === "ins-missing-expiration" ||
    issue.detectorSlug === "ins-stale-active"
  ) {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Grant 2026-07-11 — pm-entity-name-needed one-clicks as "verify" with
  // the rail's entity-name input (typing the name creates the business
  // entity + moves the loans). Slug-gated like ins-missing-expiration:
  // the generic RECON_VERBS branch would mark verify applicable with NO
  // params, which is a guaranteed 400 (entity_name is the whole action).
  if (issue.detectorSlug === "pm-entity-name-needed") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Grant 2026-07-12 — mortgage-loan-program-type-missing one-clicks as
  // "verify" with the rail's full program picker (Conventional / FHA /
  // VA / USDA / Jumbo / HELOC / Reverse / Non-QM / Other). Slug-gated
  // like the branches above: the backend requires loan_program_type, so
  // the generic RECON_VERBS branch's param-less verify would 400 —
  // and issues materialized under the old "edit the detail page then
  // mark verified" copy work immediately instead of dead-ending.
  if (issue.detectorSlug === "mortgage-loan-program-type-missing") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Baker 2026-07-13 — the pre-2018 PM revenue-gap one-clicks as "verify"
  // with the rail's amount-earned input (entering the fee IS the action).
  // Slug-gated like the branches above: the backend now requires
  // params.amount_earned_cents for this slug, so the generic RECON_VERBS
  // branch's param-less verify would 400 — and issues materialized under
  // the old "edit the detail page then mark verified" copy work
  // immediately instead of dead-ending.
  if (issue.detectorSlug === "mortgage-pre-2018-pm-revenue-gap") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Baker 2026-07-31 — realty-zero-price-transaction one-clicks as
  // "verify" with the rail's sale-price input (entering the price IS the
  // action). Slug-gated like the branches above: the backend now requires
  // params.sale_price for this slug, so the generic RECON_VERBS branch's
  // param-less verify would 400 — and, worse, before the backend required
  // it that verify resolved the card having written nothing at all.
  if (issue.detectorSlug === "realty-zero-price-transaction") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // P1-6 (cleanup UX audit, 2026-07-13) — contact-no-contact-method
  // one-clicks as "verify" with the rail's inline email/phone inputs
  // (typing at least one IS the action). Slug-gated like the branches
  // above: the backend requires at least one of email/phone_primary,
  // so the generic RECON_VERBS branch's param-less verify would 400 —
  // and issues materialized under the old "review — open the record"
  // recommendation work immediately instead of dead-ending.
  if (issue.detectorSlug === "contact-no-contact-method") {
    return { verb: "verify", ok: true };
  }
  // Baker 2026-07-13 — hh-quarantined renders its own dedicated
  // three-answer block (clear the flag / merge via the picker / archive),
  // so the generic Apply and the "needs full review" explainer are both
  // suppressed: ok:false with no `why` renders neither. Not a dead end —
  // the dedicated block IS the resolution surface.
  if (issue.detectorSlug === "hh-quarantined") {
    return { verb: "keep-current", ok: false };
  }
  // Baker 2026-07-13 — vendor-lookalike renders its own two-answer block
  // ("It's a business — retag as vendor" / "It's a person — not an
  // issue"), so suppress the generic surfaces the same way.
  if (issue.detectorSlug === "vendor-lookalike") {
    return { verb: "retag-as-vendor", ok: false };
  }
  // Donna 2026-07-30 — combined-contact renders its own two-answer block
  // ("Split into two contacts" / "Archive this combined record") — the
  // old "review" recommendation dead-ended at a contact page with no
  // split affordance. ok:false with no `why` renders neither the generic
  // Apply nor the "needs full review" explainer; the block IS the
  // resolution surface.
  if (issue.detectorSlug === "combined-contact") {
    return { verb: "verify", ok: false };
  }
  // Donna + Baker 2026-07-30 — hh-empty's archived-member variant
  // (stored recommendation verb "review") renders its own two-answer
  // block ("Un-archive the member" / "Genuinely closed — archive the
  // household"). The merge/archive variants of the same slug keep their
  // generic one-click handling below.
  if (
    issue.detectorSlug === "hh-empty" &&
    issue.recommendation?.verb === "review"
  ) {
    return { verb: "verify", ok: false };
  }
  // Grant 2026-07-13 — marital status one-clicks as "verify" with the
  // rail's marital-status picker (setting it IS the action). Slug-gated
  // like the branches above: the backend requires marital_status, so a
  // param-less generic verify would 400 — and it now routes to the
  // household's advisor, so it flows through the workspace decide rail.
  if (issue.detectorSlug === "judgment-marital-status-missing") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Baker 2026-07-31 — hh-no-primary one-clicks as "verify" with the
  // rail's member picker (picking IS the action; the backend sets the
  // pointer and demotes every other claimant atomically). Slug-gated like
  // the branches above: the backend requires params.primary_contact_id,
  // so the generic RECON_VERBS branch's param-less verify would 400.
  //
  // Replaces "open the household record and pick a member", which was a
  // dead end for the multi-primary variant — that Edit form already named
  // the right person, so re-picking them changed nothing (the write path
  // treated it as a no-op) and the item never cleared.
  if (issue.detectorSlug === "hh-no-primary") {
    return {
      verb: "verify",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // Grant 2026-08-18 — create-policy always routes through the picker
  // overlay (the rail suppresses the generic apply button whenever
  // ivansCreatePolicySource() matches, exactly like the realty link), so
  // this reports applicable rather than falling to the catch-all's
  // "needs the full review screen" dead end.
  if (rec === "create-policy") return { verb: "create-policy", ok: true };
  if (rec === "dismiss") return { verb: "dismiss", ok: true };
  // Grant 2026-09-15 — archive is one-click, and never was. The decide
  // route has handled this verb all along (company-scope re-check on the
  // REAL target record, the refusal that won't bury a household with live
  // work attached, version snapshot + audit row + a 60s restore undo), and
  // every detector that recommends it emits complete instructions. The one
  // missing piece was HERE: `applicability()` had no branch for "archive",
  // so all four archive detectors — opportunity-duplicate-open,
  // realty-household-duplicate-transaction, empty-policy-record and
  // hh-empty's empty-shell branch — fell to the catch-all at the bottom and
  // rendered 'The recommended action ("Archive the duplicate…") needs the
  // full review screen — open the record to apply it.'
  //
  // That was a dead end in BOTH directions (UX rule #5), which is why it
  // survived: the record page it sends you to has no archive-this-duplicate
  // action either, so there was nowhere the sentence could be followed.
  //
  // Params pass through verbatim. The two duplicate detectors send
  // archive_id + archive_kind, which name the redundant copy exactly; the
  // other two send only their own record id, and the route falls back to
  // the issue's primary affected record + entityKind — the same fallback
  // the hh-quarantined block's param-less act("archive") has used since
  // 2026-07-13, and correct for both (their affectedRecords[0] IS the
  // record to archive). Friction is unchanged: these are confirm-tier, so
  // the rail still requires a reason code (fetched for action class
  // "archive") before the Apply button enables.
  //
  // The alias is the same handler server-side (`case "archive": case
  // "archive-empty-household":`). It is normally auto-applied by the
  // reconcile sweep so no card appears, but it reaches this rail whenever
  // the sweep is behind — leaving it out would keep the identical dead end
  // for the one shape Grant already asked to stop seeing.
  if (rec === "archive" || rec === "archive-empty-household") {
    return {
      verb: rec as Verb,
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  if (rec === "snooze") return { verb: "snooze", ok: true };
  // acknowledge is one-click: the backend resolves the issue with the
  // acknowledgement recorded (audited, 60s reopen-undo); no params, no
  // record change. The realty BrokerMint↔AC orphan detectors recommend
  // it so the "no AC overlay needed" flag has a real button instead of
  // dead-ending at "open the record to apply it" (Nate, 2026-07-06).
  if (rec === "acknowledge") return { verb: "acknowledge", ok: true };
  // recase is one-click: the backend recomputes the normalized name
  // from the stored value (single source of truth — no params to pass).
  if (rec === "recase") return { verb: "recase", ok: true };
  // create-relationship-row is one-click: the pair is in the
  // recommendation params and the backend derives the row itself.
  if (rec === "create-relationship-row") {
    return {
      verb: "create-relationship-row",
      ok: true,
      ...(issue.recommendation?.params
        ? { params: issue.recommendation.params }
        : {}),
    };
  }
  // resolve-trust-reattach is one-click: the backend re-validates the
  // quarantined household server-side and decides attach-vs-keep.
  if (rec === "resolve-trust-reattach") {
    return {
      verb: "resolve-trust-reattach",
      ok: true,
      params: { household_id: issue.affectedRecords[0]?.id },
    };
  }
  // E1 — reconciliation disposition verbs are one-click: the backend
  // resolves the issue by id+verb (audited, undo=reopen); no params.
  // Any figure/value write rides the owning domain workflow, wired
  // per-domain in E4–E9. Verb list is the single source of truth in
  // recon-verbs.ts.
  if (rec && (RECON_VERBS as readonly string[]).includes(rec)) {
    return { verb: rec as Verb, ok: true };
  }
  if (rec === "merge") {
    const recs = issue.affectedRecords;
    if (
      recs.length === 2 &&
      recs[0] &&
      recs[1] &&
      recs[0].kind === recs[1].kind
    ) {
      const explicitSurvivor = readSurvivorId(issue);
      let survivorId = recs[0].id;
      let mergedId = recs[1].id;
      if (
        explicitSurvivor &&
        (explicitSurvivor === recs[0].id || explicitSurvivor === recs[1].id)
      ) {
        survivorId = explicitSurvivor;
        mergedId = recs[0].id === explicitSurvivor ? recs[1].id : recs[0].id;
      }
      return {
        verb: "merge",
        ok: true,
        params: { survivor_id: survivorId, merged_id: mergedId },
      };
    }
    // 2026-07-17 (Joy's duplicate-policy queue) — an N-record (3+)
    // same-kind cluster IS one-click when the detector's recommendation
    // carries an EXPLICIT survivor_id + merged_ids that together cover
    // every record in the cluster. That's precisely the backend decide
    // route's contract for service-record policy merges (it accepts
    // params.merged_ids and anti-spoofs every id against the issue's own
    // affected records), and the insurance-duplicate-policy detector has
    // emitted exactly that shape all along — the old blanket "open it
    // for full side-by-side review" pointed 3-row clusters at a review
    // screen that doesn't exist for service records (a dead end, UX rule
    // #5). Clusters WITHOUT a complete explicit recommendation keep the
    // full-review block: we never guess a survivor for 3+ records.
    if (recs.length > 2 && recs.every((r) => r.kind === recs[0]!.kind)) {
      const survivorId = readSurvivorId(issue);
      const mergedIds = readMergedIds(issue);
      if (survivorId && mergedIds) {
        const clusterIds = new Set(recs.map((r) => r.id));
        const coversCluster =
          clusterIds.has(survivorId) &&
          mergedIds.every((id) => clusterIds.has(id) && id !== survivorId) &&
          new Set([survivorId, ...mergedIds]).size === clusterIds.size;
        if (coversCluster) {
          return {
            verb: "merge",
            ok: true,
            params: { survivor_id: survivorId, merged_ids: mergedIds },
          };
        }
      }
    }
    return {
      verb: "merge",
      ok: false,
      why:
        recs.length > 2
          ? `This is a ${recs.length}-record cluster without a complete recommended survivor — review and resolve it from the records' detail pages before merging.`
          : "This needs full review before merging — open the record.",
    };
  }
  // Any other recommended verb (retag/move/restore/…): not one-click
  // here yet; surface it without a dead end.
  return {
    verb: "dismiss",
    ok: false,
    why: `The recommended action (“${issue.recommendation?.label ?? rec}”) needs the full review screen — open the record to apply it.`,
  };
}

// Plain-language label for the chosen-disposition Apply button (the
// one in "or do something else"). Names the steward's CHOICE in their
// words — "Keep separate", not "Apply: dismiss". Pure (takes the issue
// FAMILY string, not the DOM-typed CleanupIssueSummary) so it's
// node-testable alongside applicability(). — Baker 2026-06-26
export function choiceApplyLabel(
  verb: Verb,
  issueFamily: string,
  vendorAllMode: boolean,
): string {
  switch (verb) {
    case "dismiss":
      return issueFamily === "duplicate"
        ? "Keep separate — not a duplicate"
        : "Mark not an issue";
    case "snooze":
      return "Snooze for 24 hours";
    case "retag-as-vendor":
      return vendorAllMode
        ? "Tag both as vendors & keep separate"
        : "Tag as vendor & keep separate";
    default:
      return `Apply: ${verb}`;
  }
}

// Short verb-noun for the override-rationale prompt ("You're choosing
// 'keep separate' instead of the recommendation"). Mirrors
// choiceApplyLabel.
export function choiceVerbNoun(verb: Verb, issueFamily: string): string {
  switch (verb) {
    case "dismiss":
      return issueFamily === "duplicate" ? "keep separate" : "not an issue";
    case "snooze":
      return "snooze";
    case "retag-as-vendor":
      return "tag as vendor";
    default:
      return verb;
  }
}

// `recommendation.params` is `Record<string, unknown>` by contract.
// Pulling `survivor_id` out as a string defends against detectors
// that forget the convention (returns null silently rather than
// emitting `undefined`/non-string ids into the merge payload).
function readSurvivorId(issue: IssueLike): string | null {
  const raw = issue.recommendation?.params?.["survivor_id"];
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

// Same defensive read for `merged_ids` (the N-way merge contract):
// only a non-empty array of non-empty strings counts — anything else
// (absent, mixed types, empty) returns null and the cluster falls back
// to the full-review block.
function readMergedIds(issue: IssueLike): string[] | null {
  const raw = issue.recommendation?.params?.["merged_ids"];
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const ids = raw.filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
  return ids.length === raw.length ? ids : null;
}
